import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Card, CardContent } from '../ui/card';
import { Search, Filter, X } from 'lucide-react';

interface AgentFiltersProps {
  onFilterChange: (filters: AgentFilterState) => void;
  filters: AgentFilterState;
  /** Hide the category control when the caller's records carry no category (the agents page). */
  showCategory?: boolean;
}

export interface AgentFilterState {
  search: string;
  status: 'all' | 'active' | 'inactive' | 'error';
  category: string;
  /**
   * The VALUE names the measure the comparator actually uses (task-er6cn4n84j).
   * 'lastUpdated' sorted by the last EXECUTION time from agent_logs, never by the row's
   * updatedAt, and 'performance' sorted by EXECUTION COUNT, so the busiest agent ranked
   * highest whether or not it was succeeding. Both identifiers named something the code
   * did not measure, which is the same defect as the labels and one layer less visible.
   */
  sortBy: 'name' | 'status' | 'lastExecution' | 'executions';
}

export function AgentFilters({ onFilterChange, filters, showCategory = true }: AgentFiltersProps) {
  const handleSearchChange = (value: string) => {
    onFilterChange({ ...filters, search: value });
  };

  const handleStatusChange = (value: string) => {
    onFilterChange({ ...filters, status: value as AgentFilterState['status'] });
  };

  const handleCategoryChange = (value: string) => {
    onFilterChange({ ...filters, category: value });
  };

  const handleSortChange = (value: string) => {
    onFilterChange({ ...filters, sortBy: value as AgentFilterState['sortBy'] });
  };

  const clearFilters = () => {
    onFilterChange({
      search: '',
      status: 'all',
      category: 'all',
      sortBy: 'name'
    });
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <div className="flex-1">
            <Label htmlFor="search">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                placeholder="Search agents..."
                value={filters.search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          <div className="w-full lg:w-48">
            <Label htmlFor="status">Status</Label>
            <Select value={filters.status} onValueChange={handleStatusChange}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {showCategory && (
            <div className="w-full lg:w-48">
              <Label htmlFor="category">Category</Label>
              <Select value={filters.category} onValueChange={handleCategoryChange}>
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="content">Content</SelectItem>
                  <SelectItem value="analytics">Analytics</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="w-full lg:w-48">
            <Label htmlFor="sort">Sort By</Label>
            <Select value={filters.sortBy} onValueChange={handleSortChange}>
              <SelectTrigger id="sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="status">Status</SelectItem>
                {/* "Last Updated" and "Performance" named measures the comparator does not
                    use. agents.updatedAt IS available and IS the row's last modification --
                    a settings edit or a pause, never activity (types/agent-types-new.ts:20)
                    -- so the old label was not merely vague, it named a different column
                    that exists. Renamed to what is sorted rather than re-sorted: the current
                    orderings, last-run-first and busiest-first, are the useful ones. */}
                <SelectItem value="lastExecution">Last Execution</SelectItem>
                <SelectItem value="executions">Executions</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={clearFilters}
            className="h-10 w-10"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}